import { useState } from "react";

import {
  normalizedRate,
  RATE_LIMIT_CEILING_KEY,
  RATE_LIMIT_CEILING_WINDOW_SECONDS,
  rateLimitCeilingOf,
  rateLimitValuesOf,
  type McpSettingParam,
  type RateLimitApplyResponse,
  type RateLimitLane,
  type RateLimitRefusalBody,
} from "../../api/mcp";
import { useAccess } from "../../auth/useAccess";
import type { AppConfig } from "../../config";
import { useWrite } from "../../hooks/useWrite";
import { Button, Input } from "../../f7";
import { ForbiddenNotice, Gate } from "../authority/Gate";
import { Card, Notice } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";

const LANE_LABEL: Record<RateLimitLane, string> = {
  core: "Core",
  reports: "Reports",
};

/**
 * One lane's paired form: `max_requests` + `window_seconds`, submitted
 * together because the server takes them together — there is no
 * partial-update form on this route, on purpose. A single-key write could
 * set one without the other, and it is the PAIR that determines the rate
 * that actually matters.
 *
 * The live "normalizes to N per W" preview mirrors the server's own formula
 * (`normalizedRate`, matched against the doc's worked examples in
 * mcp.test.ts) so an operator sees the number BEFORE submitting — but it is
 * a preview, not a gate. The ceiling itself is a live SSM value with no
 * write path from this API at all, so the 422 stays the only real
 * enforcement; this card never blocks Apply on its own math.
 */
function LaneCard({
  config,
  lane,
  params,
  onDone,
}: {
  config: AppConfig;
  lane: RateLimitLane;
  params: McpSettingParam[];
  onDone: () => void;
}) {
  const access = useAccess();
  const canWrite = access.can("settings:write");
  const write = useWrite<RateLimitApplyResponse>(config);

  const current = rateLimitValuesOf(params, lane);
  const ceiling = rateLimitCeilingOf(params, lane);
  const ceilingWindow = RATE_LIMIT_CEILING_WINDOW_SECONDS[lane];

  const [maxRequests, setMaxRequests] = useState(current.max_requests?.toString() ?? "");
  const [windowSeconds, setWindowSeconds] = useState(current.window_seconds?.toString() ?? "");
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<RateLimitApplyResponse | null>(null);

  const maxRequestsN = Number(maxRequests);
  const windowSecondsN = Number(windowSeconds);
  const bothPositiveInts =
    /^\d+$/.test(maxRequests.trim()) &&
    /^\d+$/.test(windowSeconds.trim()) &&
    maxRequestsN > 0 &&
    windowSecondsN > 0;

  const dirty =
    bothPositiveInts &&
    (maxRequestsN !== current.max_requests || windowSecondsN !== current.window_seconds);

  const preview = bothPositiveInts ? normalizedRate(maxRequestsN, windowSecondsN, lane) : null;
  // A preview, never a submit gate — see the docstring above. Still shown in
  // red so a value about to be refused does not look identical to a safe one.
  const overCeiling = preview !== null && ceiling !== null && preview > ceiling;

  function submit(reason: string) {
    void write
      .submit(`/mcp/admin/ratelimit/${lane}`, {
        method: "PATCH",
        body: JSON.stringify({
          max_requests: maxRequestsN,
          window_seconds: windowSecondsN,
          ...(reason ? { reason } : {}),
        }),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setOpen(false);
        onDone();
      });
  }

  // The 422's extra fields — `ApiError.body` is `unknown`-shaped by
  // construction, so every field is read through a guard rather than trusted.
  const refusal =
    write.state.kind === "invalid" ? (write.state.error.body as RateLimitRefusalBody) : null;

  return (
    <Card
      title={`${LANE_LABEL[lane]} lane`}
      note={
        ceiling !== null ? (
          <>
            Ceiling <span className="ref">{ceiling}</span> per {ceilingWindow}s (
            <span className="ref">{RATE_LIMIT_CEILING_KEY[lane]}</span>) — read-only, CDK-owned.
          </>
        ) : (
          "Ceiling not seeded yet."
        )
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-32">
          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
            Max requests
          </label>
          <Input
            value={maxRequests}
            disabled={!canWrite}
            onChange={(e) => setMaxRequests(e.target.value)}
            inputMode="numeric"
            className="font-mono"
            aria-label={`${lane} max requests`}
          />
        </div>
        <div className="w-32">
          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
            Window (seconds)
          </label>
          <Input
            value={windowSeconds}
            disabled={!canWrite}
            onChange={(e) => setWindowSeconds(e.target.value)}
            inputMode="numeric"
            className="font-mono"
            aria-label={`${lane} window seconds`}
          />
        </div>
        {dirty && (
          <Gate need="settings:write">
            <Button onClick={() => setOpen(true)}>Save</Button>
          </Gate>
        )}
      </div>

      <div className="mt-2 space-y-1 text-xs">
        {!bothPositiveInts && (maxRequests || windowSeconds) && (
          <p className="text-red-600 dark:text-red-400">
            Both must be whole numbers greater than zero.
          </p>
        )}
        {preview !== null && (
          <p className={overCeiling ? "text-red-600 dark:text-red-400" : "text-slate-500 dark:text-slate-400"}>
            Normalizes to <span className="ref">{preview.toLocaleString()}</span> per{" "}
            {ceilingWindow}s
            {ceiling !== null && <> — ceiling is {ceiling.toLocaleString()}</>}
            {overCeiling && ". This would be refused."}
          </p>
        )}
        {current.max_requests === null && (
          <p className="text-slate-500 dark:text-slate-400">
            Not seeded — the code default applies until this is set.
          </p>
        )}
      </div>

      {write.state.kind === "denied" && (
        <div className="mt-3">
          <ForbiddenNotice message={write.state.error.message} />
        </div>
      )}
      {write.state.kind === "invalid" && (
        <div className="mt-3">
          <Notice tone="red" title="Refused — nothing was written">
            <p>{write.state.error.message}</p>
            {refusal?.normalized_rate !== undefined && refusal.ceiling !== undefined && (
              <p className="mt-1 text-xs">
                {refusal.normalized_rate.toLocaleString()} per{" "}
                {refusal.ceiling_window_seconds ?? ceilingWindow}s exceeds the ceiling of{" "}
                {refusal.ceiling.toLocaleString()}.
              </p>
            )}
          </Notice>
        </div>
      )}

      {result?.applied && (
        <div className="mt-3">
          <WriteResult title={`${LANE_LABEL[lane]} lane updated`} onDismiss={() => setResult(null)}>
            <p>
              {Object.entries(result.applied)
                .map(([k, v]) => `${k.replace("ratelimit/", "")}: ${v.before} → ${v.value}`)
                .join(" · ")}
              . Normalizes to {result.normalized_rate.toLocaleString()} per{" "}
              {result.ceiling_window_seconds}s, ceiling {result.ceiling.toLocaleString()}.{" "}
              {effectFromSeconds(undefined, "This")}
            </p>
            <p className="mt-1 text-xs">{result.effect}</p>
          </WriteResult>
        </div>
      )}

      <ConfirmDialog
        open={open}
        title={`Change the ${LANE_LABEL[lane].toLowerCase()} rate limit`}
        confirmLabel="Save"
        requireReason={false}
        reasonHint="e.g. easing a bulk-import spike"
        busy={write.state.kind === "submitting"}
        onCancel={() => setOpen(false)}
        onConfirm={(reason) => submit(reason)}
        effect="Staged until POST /admin/settings/apply (or the next task restart) — same timing as any other settings change. Both values are required every write; there is no partial update."
      >
        <p>
          <span className="ref">max_requests</span>: {current.max_requests ?? "not set"} →{" "}
          {maxRequestsN}
        </p>
        <p>
          <span className="ref">window_seconds</span>: {current.window_seconds ?? "not set"} →{" "}
          {windowSecondsN}
        </p>
        {preview !== null && (
          <p className="mt-2 text-xs">
            Normalizes to {preview.toLocaleString()} per {ceilingWindow}s
            {ceiling !== null && <>, ceiling {ceiling.toLocaleString()}</>}. The server checks
            this exact rate, not either number alone.
          </p>
        )}
      </ConfirmDialog>
    </Card>
  );
}

/**
 * Two lanes, one card each — `core` and `reports` — replacing the four
 * independent number fields the old Settings page used to render for these
 * keys.
 *
 * Why two-and-two rather than four: MCP moved rate limits off
 * `PATCH /admin/settings` entirely onto their own paired route,
 * `PATCH /admin/ratelimit/{lane}`, because a single-key write could set
 * `max_requests` without `window_seconds` (or the reverse) — and a pair like
 * `max_requests=90, window_seconds=1` normalizes to 5400/min, which is what
 * actually trips Foodics' shared-egress IP block. The four old keys
 * (`ratelimit/max_requests`, `window_seconds`, `reports_max_requests`,
 * `reports_window_seconds`) still show up in `GET /admin/settings` — with
 * `writable: false` now — so `Settings.tsx` renders them read-only, pointing
 * here.
 */
export function RateLimitEditor({
  config,
  params,
  onDone,
}: {
  config: AppConfig;
  params: McpSettingParam[];
  onDone: () => void;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {(["core", "reports"] as const).map((lane) => (
        <LaneCard key={lane} config={config} lane={lane} params={params} onDone={onDone} />
      ))}
    </div>
  );
}
